import type { UseFormSetError } from 'react-hook-form';

/**
 * Set server-side validation errors on form fields
 */
export function setServerErrors(setError: UseFormSetError<any>, errors: Record<string, string | string[]>) {
  Object.entries(errors).forEach(([field, message]) => {
    setError(field, {
      type: 'server',
      message: Array.isArray(message) ? message[0] : message,
    });
  });
}

/**
 * Parse API validation errors and set them on form fields
 *
 * Handles both single error messages and arrays of errors per field.
 */
export function parseApiValidationErrors(apiErrors: any): Record<string, string> {
  const errors: Record<string, string> = {};

  if (typeof apiErrors === 'object' && apiErrors !== null) {
    Object.entries(apiErrors).forEach(([field, message]) => {
      if (Array.isArray(message)) {
        errors[field] = message[0];
      } else if (typeof message === 'string') {
        errors[field] = message;
      }
    });
  }

  return errors;
}

/**
 * Result of handling server errors with field mapping
 */
export interface HandleServerErrorResult {
  mappedErrors: Record<string, string>;
  unmatchedErrors: string[];
  hasMappedErrors: boolean;
  hasUnmatchedErrors: boolean;
  fallbackMessage: string;
}

/**
 * Handle server validation errors by mapping API field names to form field names
 */
export function mapValidationErrors(
  validationErrors: Record<string, string | string[]> | undefined,
  fieldMap: Record<string, string>,
  fallbackMessage = 'Validation failed'
): HandleServerErrorResult {
  const mappedErrors: Record<string, string> = {};
  const unmatchedErrors: string[] = [];

  if (validationErrors) {
    Object.entries(validationErrors).forEach(([field, message]) => {
      const formField = fieldMap[field];
      const errorMessage = Array.isArray(message) ? message[0] : message;

      if (formField) {
        mappedErrors[formField] = errorMessage;
      } else {
        unmatchedErrors.push(errorMessage);
      }
    });
  }

  return {
    mappedErrors,
    unmatchedErrors,
    hasMappedErrors: Object.keys(mappedErrors).length > 0,
    hasUnmatchedErrors: unmatchedErrors.length > 0,
    fallbackMessage,
  };
}


